/**
 * ReferenceLine.js
 * 
 * This module provides functionality for creating and managing reference lines
 * in a 3D environment. Reference lines help users align objects by creating
 * either perpendicular (right angle) or collinear (in-line) snap points relative 
 * to existing line segments.
 */

import {DrawingGlobals} from "../../Global/variables";
import {LINES} from "../../Global/materials";
import * as THREE from "three";

/**
 * Determines if the current mouse position should snap to a reference line point
 * 
 * This function checks if the mouse should snap to points that are:
 * 1. Perpendicular (90 degrees) to an existing line
 * 2. Collinear (in-line) with an existing line
 * 
 * It uses a 0.1 unit threshold distance for snapping.
 * 
 * @param {THREE.Vector3|null} prevRefPoint - The previously snapped reference point, if any
 * @param {THREE.Vector3} mousePosition - The current mouse position in 3D space
 * @returns {THREE.Vector3|null} The reference point to snap to, or null if no snap should occur
 */
export function refLineSnap(prevRefPoint, mousePosition) {
    console.log("Drawing line");
    
    // Snapping threshold distance (in world units)
    const THRESHOLD = 0.03;
    
    // If we're already snapped to a reference point and still within threshold, keep using it
    if (prevRefPoint != null && mousePosition.distanceTo(prevRefPoint) <= THRESHOLD) {
        return prevRefPoint;
    } else {
        // Get the first two points of the current line/polyline
        let a = DrawingGlobals.intersectionPoints[0]; // Starting point
        let b = DrawingGlobals.intersectionPoints[1]; // Second point
        
        // Calculate potential snap points
        let rightAnglePoint = findRightAnglePoint(a, b, mousePosition); // Point perpendicular to line AB
        let collinearPoint = findCollinearPoint(a, b, mousePosition);   // Point in-line with line AB
        
        // Check if mouse is close enough to the right angle (perpendicular) snap point
        if (mousePosition.distanceTo(rightAnglePoint) <= THRESHOLD) {
            return rightAnglePoint;
        } 
        // Check if mouse is close enough to the collinear snap point
        else if (mousePosition.distanceTo(collinearPoint) <= THRESHOLD) {
            return collinearPoint;
        } 
        // If mouse is not close to any snap point, hide the reference line and return null
        else {
            LINES.referenceLine.visible = false;
            return null;
        }
    }
}

/**
 * Calculates a point that creates a right angle (90 degrees) with line AB,
 * while maintaining the same distance from A as the mouse position
 * 
 * This function projects the mouse position onto a plane perpendicular to
 * the AB line segment, creating a point that:
 * 1. Forms a 90-degree angle with line AB
 * 2. Is the same distance from A as the mouse position
 * 
 * @param {THREE.Vector3} a - Starting point of reference line
 * @param {THREE.Vector3} b - Second point of reference line
 * @param {THREE.Vector3} mousePosition - Current mouse position
 * @returns {THREE.Vector3} The calculated right angle snap point
 */
function findRightAnglePoint(a, b, mousePosition) {
    console.log("Finding angle");
    
    // Calculate the direction vector from A to B and normalize it
    let abDirection = new THREE.Vector3().copy(b).sub(a);
    abDirection.normalize();
    
    // Calculate the direction vector from A to mouse position and normalize it
    let mouseDirection = new THREE.Vector3().copy(mousePosition).sub(a);
    mouseDirection.normalize();
    
    // Get the distance from A to the mouse position
    let distanceToMouse = a.distanceTo(mousePosition);
    
    // Project the mouse direction onto the AB direction
    // This finds how much of the mouse direction is along the AB line
    let projectedLength = mouseDirection.dot(abDirection);
    
    // Scale the AB direction by the projected length
    // This gives us the component of the mouse direction that's parallel to AB
    let scaledABDirection = abDirection.clone().multiplyScalar(projectedLength);
    
    // Subtract the parallel component from the mouse direction
    // This gives us the perpendicular (right angle) component
    let perpendicularVector = new THREE.Vector3().copy(mouseDirection).sub(scaledABDirection);
    
    // Create the right angle point by starting at A and moving in the perpendicular
    // direction by the same distance as from A to mouse
    return new THREE.Vector3().copy(a).add(perpendicularVector.normalize().multiplyScalar(distanceToMouse));
}

/**
 * Calculates a point that is collinear (in-line) with line AB,
 * at the same distance from A as the mouse position
 * 
 * This function extends the line AB in either direction, creating a point that:
 * 1. Lies along the same line as AB
 * 2. Is the same distance from A as the mouse position
 * 3. Maintains the same z-coordinate as the mouse position
 * 
 * @param {THREE.Vector3} a - Starting point of reference line
 * @param {THREE.Vector3} b - Second point of reference line
 * @param {THREE.Vector3} mousePosition - Current mouse position
 * @returns {THREE.Vector3} The calculated collinear snap point
 */
function findCollinearPoint(a, b, mousePosition) {
    // Calculate the distance from A to the mouse position
    let distance = a.distanceTo(mousePosition);
    console.log("Finding disatance", distance);
    
    
    // Calculate the direction vector from A to B
    let abVector = new THREE.Vector3().copy(b).sub(a);
    
    // Get the normalized direction of the AB line
    let direction = abVector.clone().normalize();
    
    // Get the direction from A to the mouse position
    let directionToMouse = new THREE.Vector3().copy(mousePosition).sub(a).normalize();
    
    // Calculate the dot product to determine if mouse is in the same
    // general direction as B from A, or in the opposite direction
    let dotProduct = direction.dot(directionToMouse);
    
    // If the dot product is negative, mouse is in the opposite direction,
    // so we need to flip the direction vector
    if (dotProduct < 0) {
        direction.negate();
    }
    
    // Scale the direction vector by the distance to the mouse
    let scaledDirection = direction.clone().multiplyScalar(distance);
    
    // Create the collinear point by starting at A and moving in the calculated direction
    let collinearPoint = new THREE.Vector3().copy(a).add(scaledDirection);
    
    // Maintain the z-coordinate of the mouse position
    // This is important for working in 3D space while constraining to a specific plane
    collinearPoint.z = mousePosition.z;
    
    return collinearPoint;
}

/**
 * Updates and displays a reference line from the first intersection point to the given point
 * 
 * This function sets the start point of the reference line at the first drawing point,
 * and the end point at the current calculated snap point.
 * 
 * @param {THREE.Vector3} point - The end point for the reference line
 */
export function showReferenceLine(point) {
    // Get the first intersection point (starting point of the drawing)
    const intersectionPoint = DrawingGlobals.intersectionPoints[0];
    console.log("SHOWINF RED LINE");
    
    // Get the position attribute array of the reference line geometry
    const positionArray = LINES.referenceLine.geometry.attributes.position.array;
    
    // Update the start point (index 0-2) of the line to match the first intersection point
    positionArray[0] = intersectionPoint.x;
    positionArray[1] = intersectionPoint.y;
    positionArray[2] = intersectionPoint.z;
    
    // Update the end point (index 3-5) of the line to match the given point
    positionArray[3] = point.x;
    positionArray[4] = point.y;
    positionArray[5] = point.z;
    
    // Make the reference line visible
    LINES.referenceLine.visible = true;
    
    // Compute line distances for proper rendering of dashed/dotted lines
    LINES.referenceLine.computeLineDistances();
    
    // Mark the position attribute as needing update
    LINES.referenceLine.geometry.attributes.position.needsUpdate = true;
}